import {
  CASE_MANAGER,
  DISCHARGE_REASON,
  EPISODE_CLASSIFICATION,
  filterKeysToWords,
  GROUP_ID,
  HEALTH_PLAN_CLASSIFICATION,
  HOSPITAL,
  HOSPITAL_CLIENT,
  LATEST_REHAB_STATE,
  LOCATION,
  LOCATION_TYPE,
  NONE_OPTION,
  ONLY_OTHER_ORGANIZATIONS,
  OWNER,
  OWNER_CLIENT,
  PAYER,
  PHYSICIAN_GROUP,
  PHYSICIAN_TEAM,
  PLAN_TYPE_CLASSIFICATION,
  REFERRED_BY_ID,
  STATUS_OUT_OF_DATE,
  UTILIZATION_MANAGER,
} from 'constants/filterKeysConstants';
import { PHYSICIAN_GROUP as PHYS_GROUP_API_TYPE, PHYSICIAN_TEAM as PHYS_TEAM_API_TYPE } from 'constants/locationTypes';
import { capitalize } from 'lib/util';
import { ClassificationApiName } from 'models/Classification';
import { ClientType } from 'models/Client';
import GroupType from 'models/GroupType';
import Profile from 'models/Profile';
import RehabState from 'models/RehabState';
import { ReviewStatus } from 'models/Review';
import User from 'models/User';
import { useInfiniteAttrValues } from 'services/api/attrValue';
import { useInfiniteClassifications } from 'services/api/classification';
import { useInfiniteClientOptions } from 'services/api/client';
import { useInfiniteDischargeReasons, usePlanningDischargeOptions } from 'services/api/dischargeReasons';
import { GroupAccessMode, useInfiniteGroups } from 'services/api/group';
import { useInfiniteLocations } from 'services/api/location';
import { useSearchableRehabStates } from 'services/api/rehabStates';
import { useInfiniteUsers } from 'services/api/user';
import { FilterOption } from 'stores/types';
import colors from 'styles/theme/colors';
import NoteIcon from 'svg/NoteIcon';

// Slideout Section Title constants
export const COMANAGEMENT_TEAM = 'Co-Management Team';
export const ADDITIONAL_FILTERS = 'Additional Filters';

type FilterDropdownConfig = {
  dataCy?: string;
  placeholder: string;
  name: string;
  getOptionLabel: (obj: any) => string;
  getOptionValue: (obj: any) => string;
  filterType: FILTER_TYPES;
  options?: FilterOption[];
  getOptions?: {
    hook: any;
    params?: object;
    optionsToPrepend?: any[];
  };
};

export enum FILTER_TYPES {
  DROPDOWN = 'dropdown',
  CHECKBOX = 'checkbox',
}

export const defaultFilterDropdownConfig = {
  getOptionLabel: (obj) => obj.name,
  getOptionValue: (obj) => obj.id,
  filterType: FILTER_TYPES.DROPDOWN,
};

export type FilterDropdown = typeof defaultFilterDropdownConfig & {
  placeholder: string;
  name: string;
  getOptions: {
    hook: (...args: any[]) => any;
    params?: object;
    optionsToPrepend?: any[];
  };
};

// Static Dropdowns
export const DISCHARGE_REASON_DROPDOWN: FilterDropdownConfig = {
  ...defaultFilterDropdownConfig,
  getOptionLabel: (obj) => obj.displayName,
  placeholder: 'Discharged To',
  name: DISCHARGE_REASON,
  getOptions: {
    hook: useInfiniteDischargeReasons,
    params: {
      sortBy: 'order asc',
    },
  },
};

export const PHYSICIAN_GROUP_DROPDOWN: FilterDropdownConfig = {
  ...defaultFilterDropdownConfig,
  placeholder: 'Physician Group',
  name: PHYSICIAN_GROUP,
  getOptions: {
    hook: useInfiniteGroups,
    params: {
      type: PHYS_GROUP_API_TYPE,
      sortBy: 'name asc',
    },
  },
};

export const HOSPITAL_DROPDOWN: FilterDropdownConfig = {
  ...defaultFilterDropdownConfig,
  placeholder: 'Hospital',
  name: HOSPITAL,
  getOptions: {
    hook: useInfiniteGroups,
    params: {
      type: HOSPITAL,
      sortBy: 'name asc',
    },
    optionsToPrepend: [NONE_OPTION],
  },
};

export const HOSPITAL_CLIENT_DROPDOWN: FilterDropdownConfig = {
  ...defaultFilterDropdownConfig,
  placeholder: 'Referral Source',
  name: HOSPITAL_CLIENT,
  getOptions: {
    hook: useInfiniteClientOptions,
    params: {
      clientType: ClientType.HEALTH_SYSTEM,
      sortBy: 'name asc',
      viewOnly: true,
    },
    optionsToPrepend: [NONE_OPTION],
  },
};

export const PHYSICIAN_TEAM_DROPDOWN: FilterDropdownConfig = {
  ...defaultFilterDropdownConfig,
  placeholder: 'Physician',
  name: PHYSICIAN_TEAM,
  getOptionValue: (obj) => obj.name,
  getOptions: {
    hook: useInfiniteAttrValues,
    params: {
      distinctDisplayName: true,
      type: PHYS_TEAM_API_TYPE,
      active: true,
    },
    optionsToPrepend: [NONE_OPTION],
  },
};

export const REFERRED_BY_DROPDOWN: FilterDropdownConfig = {
  ...defaultFilterDropdownConfig,
  placeholder: 'Referred By',
  getOptionLabel: (obj) => (obj.groupType.displayName ? `${obj.name} (${obj.groupType.displayName})` : obj.name),
  name: REFERRED_BY_ID,
  getOptions: {
    hook: useInfiniteGroups,
    params: {
      sortBy: 'name asc',
      include: 'group_type',
      isProvider: true,
      // Limit to groups the user can access: the default also pulls in the whole
      // client-network, which floods the filter with groups absent from the list.
      accessModes: [GroupAccessMode.Allowed],
    },
  },
};

export const REFERRED_TO_DROPDOWN: FilterDropdownConfig = {
  ...defaultFilterDropdownConfig,
  placeholder: 'Referred To',
  getOptionLabel: (obj) => (obj.groupType.displayName ? `${obj.name} (${obj.groupType.displayName})` : obj.name),
  name: GROUP_ID,
  getOptions: {
    hook: useInfiniteGroups,
    params: {
      sortBy: 'name asc',
      include: 'group_type',
      viewOnly: true,
      isProvider: true,
      // Limit to groups the user can access: the default also pulls in the whole
      // client-network, which floods the filter with groups absent from the list.
      accessModes: [GroupAccessMode.Allowed],
    },
  },
};

export const REVIEW_STATUS_DROPDOWN: FilterDropdownConfig = {
  ...defaultFilterDropdownConfig,
  placeholder: 'Status',
  name: 'status',
  options: Object.values(ReviewStatus).map((status) => ({
    id: status,
    name: status === ReviewStatus.REJECTED ? 'Denied' : capitalize(status),
  })),
};

export const createLevelOfCareDropdown = (providerTypes: GroupType[]): FilterDropdownConfig => ({
  ...defaultFilterDropdownConfig,
  placeholder: 'Level of Care',
  name: LOCATION_TYPE,
  options: providerTypes.map((pt) => ({ id: pt.apiName, name: pt.displayName })),
});

export const LOCATION_DROPDOWN: FilterDropdownConfig = {
  ...defaultFilterDropdownConfig,
  placeholder: 'Location',
  name: LOCATION,
  getOptions: {
    hook: useInfiniteLocations,
    params: {
      sortBy: 'name asc',
    },
  },
};

export const PAYER_DROPDOWN: FilterDropdownConfig = {
  ...defaultFilterDropdownConfig,
  placeholder: 'Payer',
  name: PAYER,
  getOptions: {
    hook: useInfiniteGroups,
    params: {
      type: PAYER,
      sortBy: 'name asc',
    },
  },
};

export const HEALTH_PLAN_DROPDOWN: FilterDropdownConfig = {
  ...defaultFilterDropdownConfig,
  placeholder: filterKeysToWords[HEALTH_PLAN_CLASSIFICATION],
  name: HEALTH_PLAN_CLASSIFICATION,
  getOptions: {
    hook: useInfiniteClassifications,
    params: {
      type: ClassificationApiName.HEALTH_PLAN,
      sortBy: 'name asc',
    },
    optionsToPrepend: [NONE_OPTION],
  },
};

export const PLAN_TYPE_DROPDOWN: FilterDropdownConfig = {
  ...defaultFilterDropdownConfig,
  placeholder: filterKeysToWords[PLAN_TYPE_CLASSIFICATION],
  name: PLAN_TYPE_CLASSIFICATION,
  getOptions: {
    hook: useInfiniteClassifications,
    params: {
      type: ClassificationApiName.PLAN_TYPE,
      sortBy: 'name asc',
    },
    optionsToPrepend: [NONE_OPTION],
  },
};

export const EPISODE_TYPE_DROPDOWN: FilterDropdownConfig = {
  ...defaultFilterDropdownConfig,
  placeholder: filterKeysToWords[EPISODE_CLASSIFICATION],
  name: EPISODE_CLASSIFICATION,
  getOptions: {
    hook: useInfiniteClassifications,
    params: {
      type: ClassificationApiName.EPISODE,
      sortBy: 'name asc',
    },
    optionsToPrepend: [NONE_OPTION],
  },
};

export const LATEST_REHAB_STATE_DROPDOWN: FilterDropdownConfig = {
  ...defaultFilterDropdownConfig,
  placeholder: 'Stage',
  name: LATEST_REHAB_STATE,
  dataCy: 'stage',
  getOptionLabel: (rehabState: RehabState) => rehabState.state,
  getOptions: {
    hook: useSearchableRehabStates,
  },
};

const getUserOptionLabel = (option: User | typeof NONE_OPTION) =>
  option instanceof User ? option.fullName : option.name;

export const CASE_MANAGER_DROPDOWN = {
  ...defaultFilterDropdownConfig,
  placeholder: filterKeysToWords[CASE_MANAGER],
  name: CASE_MANAGER,
  getOptionLabel: getUserOptionLabel,
  getOptions: {
    hook: useInfiniteUsers,
    params: {
      include: 'credential',
      sortBy: 'name asc',
      activeOrUserRole: 'case_manager',
    },
    optionsToPrepend: [NONE_OPTION],
  },
};

export const UTILIZATION_MANAGER_DROPDOWN: FilterDropdownConfig = {
  ...defaultFilterDropdownConfig,
  placeholder: filterKeysToWords[UTILIZATION_MANAGER],
  name: UTILIZATION_MANAGER,
  getOptionLabel: getUserOptionLabel,
  getOptions: {
    hook: useInfiniteUsers,
    params: {
      include: 'credential',
      sortBy: 'name asc',
      activeOrUserRole: 'utilization_manager',
    },
    optionsToPrepend: [NONE_OPTION],
  },
};

// Dynamic Dropdowns. Use for creating a dropdown config for a provider.
export const createGroupTypeDropdown = (
  providerType: GroupType,
  additionalParams?: object,
  overrides?: { placeholder?: string; name?: string }
): FilterDropdownConfig => ({
  ...defaultFilterDropdownConfig,
  placeholder: overrides?.placeholder ?? providerType.displayName,
  name: overrides?.name ?? providerType.camelCaseApiName,
  getOptions: {
    hook: useInfiniteGroups,
    params: {
      groupType: providerType.id,
      sortBy: 'name asc',
      ...additionalParams,
    },
  },
});

export const createGroupTypesDropdowns = (groupTypes: GroupType[]) =>
  groupTypes.map((gt) => createGroupTypeDropdown(gt));

export const getComanagementTeamManagerFilterDropdowns = (profile: Profile): FilterDropdownConfig[] => {
  let dropdownToAppend: FilterDropdownConfig | undefined;

  const dropdowns = profile.actingClient.managerGroupTypesForFilters.map((gt) => createGroupTypeDropdown(gt));

  // Hospital can be optional, so we need to prepend the NONE_OPTION to the dropdown
  dropdowns.forEach((dropdown) => {
    if (dropdown.name === HOSPITAL && dropdown.getOptions) {
      dropdown.getOptions.optionsToPrepend = [NONE_OPTION];
    }
  });

  if (profile.canActAsManager) {
    dropdownToAppend = PHYSICIAN_TEAM_DROPDOWN;
  }

  if (dropdownToAppend) dropdowns.push(dropdownToAppend);

  return dropdowns;
};

export const createClientDropdown = (
  groupTypeIds: string[],
  label: string,
  filterKey: string,
  additionalParams?: object
): FilterDropdownConfig => ({
  ...defaultFilterDropdownConfig,
  placeholder: label,
  name: filterKey,
  getOptions: {
    hook: useInfiniteClientOptions,
    params: {
      sortBy: 'name asc',
      groupType: groupTypeIds,
      viewOnly: true,
      ...additionalParams,
    },
  },
});

export const createPlanningDischargeToDropdown = (groupTypeId: string) => {
  return {
    ...defaultFilterDropdownConfig,
    placeholder: 'Planning Discharged To',
    name: 'planningDischargeTo',
    getOptions: {
      hook: usePlanningDischargeOptions,
      params: {
        sortBy: 'order asc',
        groupType: groupTypeId,
        enabled: false,
      },
      optionsToPrepend: [NONE_OPTION],
    },
  };
};

export const getClientFilterDropdowns = (selectedGroupType: GroupType, profile: Profile) => [
  ...(profile.canActAsManager
    ? [
        createClientDropdown(
          [selectedGroupType.id],
          `${selectedGroupType.displayName} Company`,
          `${selectedGroupType.camelCaseApiName}Client`,
          { groupTypeType: 'provider' }
        ),
      ]
    : []),
  ...(profile.canActAsProvider
    ? [createClientDropdown([selectedGroupType.id], 'Organization', OWNER_CLIENT, { groupTypeType: 'manager' })]
    : []),
];

export const getOwnerFilterDropdown = (actingClientId: string) => {
  return {
    ...defaultFilterDropdownConfig,
    placeholder: 'Managed By',
    name: OWNER,
    getOptions: {
      hook: useInfiniteGroups,
      params: {
        clientId: actingClientId,
        isManager: true,
        'groupTypeApiName.not': 'affiliate,physician_team',
        sortBy: 'name asc',
      },
    },
  };
};

export const sortFilterDropdowns = (filterDropdowns: { placeholder: string; [key: string]: any }[]) => {
  return filterDropdowns.sort((a, b) => {
    return a.placeholder.localeCompare(b.placeholder);
  });
};

// Checkboxes
export const STATUS_OUT_OF_DATE_CHECKBOX = {
  name: STATUS_OUT_OF_DATE,
  label: 'Needs Progress Update',
  filterType: FILTER_TYPES.CHECKBOX,
};

export const HAS_NEW_CHANGES_CHECKBOX = {
  name: 'hasNewChanges',
  label: 'New Referral Information',
  labelIcon: (
    <>
      &#40;
      <NoteIcon color={colors.blue100} />
      &#41;
    </>
  ),
  filterType: FILTER_TYPES.CHECKBOX,
};

export const ONLY_OTHER_ORGANIZATIONS_CHECKBOX = {
  name: ONLY_OTHER_ORGANIZATIONS,
  label: 'Only Patients Followed by Other Organizations',
  filterType: FILTER_TYPES.CHECKBOX,
};

export const REMOVED_FROM_CENSUS_CHECKBOX = {
  name: 'removedFromCensus',
  label: 'Patients Ready to Discharge',
  filterType: FILTER_TYPES.CHECKBOX,
};
